// F1 Driver Detail Page
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useF1Driver } from '../../hooks/useF1Driver';
import { useGrillTheGrid } from '../../hooks/useGrillTheGrid';
import F1PageTemplate from '../../components/f1/F1PageTemplate';
import { TableChart } from '../../components/charts';
import LoadingSpinner from '../../components/LoadingSpinner';

const F1DriverDetailPage = () => {
  const { driverId } = useParams();
  const { driver, loading, error } = useF1Driver(driverId);
  const { getStandingsByYear } = useGrillTheGrid();

  if (loading) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <LoadingSpinner message="Loading Driver Data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold mb-4">Error Loading Driver</div>
          <div className="theme-secondary">{error}</div>
          <Link to="/f1/drivers" className="mt-4 inline-block px-4 py-2 rounded-md">
            Back to Drivers
          </Link>
        </div>
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold theme-primary mb-4">Driver Not Found</div>
          <div className="theme-secondary mb-4">Driver with ID {driverId} could not be found.</div>
          <Link to="/f1/drivers" className="inline-block px-4 py-2 rounded-md">
            Back to Drivers
          </Link>
        </div>
      </div>
    );
  }

  const stats = [
    [
      { value: driver.seasonsParticipated, label: 'Seasons Participated', group: 'Career Overview' },
      { value: driver.totalPoints, label: 'Career Points', group: 'Career Overview' },
      { value: driver.totalWins, label: 'Career Wins', group: 'Career Overview' },
      ...(driver.careerChampionships > 0 ? [{ value: driver.careerChampionships, label: 'Career Championships', group: 'Career Overview' }] : [])
    ],
    [
      { value: driver.careerBestQualifying, label: 'Career Best Qualifying', group: 'Qualifying Statistics' },
      { value: driver.careerWorstQualifying, label: 'Career Worst Qualifying', group: 'Qualifying Statistics' },
      { value: driver.careerQualifyingAverage, label: 'Career Qualifying Avg', group: 'Qualifying Statistics' },
      ...(driver.careerPoles > 0 ? [{ value: driver.careerPoles, label: 'Career Poles', group: 'Qualifying Statistics' }] : [])
    ],
    [
      { value: driver.careerBestResult, label: 'Career Best Result', group: 'Race Results' },
      { value: driver.careerWorstResult, label: 'Career Worst Result', group: 'Race Results' },
      { value: driver.careerResultAverage, label: 'Career Result Avg', group: 'Race Results' },
      ...(driver.careerPodiums > 0 ? [{ value: driver.careerPodiums, label: 'Career Podiums', group: 'Race Results' }] : [])
    ],
    [
      { value: driver.bestCircuit, label: 'Best Circuit', group: 'Circuit Performance' },
      { value: driver.bestCircuitAvg, label: 'Average Position at Best Circuit', group: 'Circuit Performance' }

    ]
  ];

  return (
    <F1PageTemplate
      title={`${driver.forename} ${driver.surname}`}
      subtitle="Driver Profile and Career Statistics"
      summary={['Personal information', 'Career statistics', 'Race history']}
      stats={stats}
    >
      <div className="space-y-8">
        {/* Driver Information */}
        <div className="card">
          <h3 className="text-xl font-semibold theme-primary mb-4">Driver Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <span className="font-medium theme-secondary">Driver ID:</span>
              <span className="ml-2 theme-primary">{driver.driverId}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Full Name:</span>
              <span className="ml-2 theme-primary">{driver.fullName}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Nationality:</span>
              <span className="ml-2 theme-primary">{driver.nationality || 'Unknown'}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Date of Birth:</span>
              <span className="ml-2 theme-primary">{driver.dob || 'Unknown'}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Driver Number:</span>
              <span className="ml-2 theme-primary">{driver.number || 'N/A'}</span>
            </div>
            <div>
              <span className="font-medium theme-secondary">Code:</span>
              <span className="ml-2 theme-primary">{driver.code || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Championship Standings */}
        {driver.standings && driver.standings.length > 0 && (
          <TableChart 
            data={driver.standings.map(standing => ({
              ...standing,
              year: (
                <Link 
                  to={`/f1/seasons/${standing.year}`}
                  className="theme-primary hover:theme-accent hover:underline font-medium"
                >
                  {standing.year}
                </Link>
              )
            }))}
            title="Season Championship Standings"
            subtitle="View driver's final standings at the end of each season"
            config={{
              columns: [
                { key: 'year', label: 'Season' },
                { key: 'position', label: 'Final Position' },
                { key: 'points', label: 'Points' },
                { key: 'wins', label: 'Wins' }
              ],
              sortable: true,
              sortBy: 'year',
              sortDirection: 'desc',
              pagination: true,
              pageSize: 10
            }}
          />
        )}

        {/* Race History */}
        {driver.races && driver.races.length > 0 && (
          <div className="card">
            <h3 className="text-xl font-semibold theme-primary mb-4">Race History</h3>
            <TableChart 
              data={driver.races.map(race => ({
                ...race,
                qualifying: race.qualifying?.position || 'N/A',
                position: race.result?.positionText || race.result?.position || 'N/A'
              }))}
              config={{
                columns: [
                  { key: 'year', label: 'Year' },
                  { key: 'round', label: 'Round' },
                  { key: 'name', label: 'Race Name' },
                  { key: 'circuit', label: 'Circuit' },
                  { key: 'qualifying', label: 'Qualifying Pos' },
                  { key: 'position', label: 'Final Position' }
                ],
                sortable: true,
                sortBy: 'year',
                sortDirection: 'desc',
                pagination: true,
                pageSize: 10
              }}
            />
          </div>
        )}

        {/* Circuit Averages */}
        {driver.circuitAverages && driver.circuitAverages.length > 0 && (
          <div className="card">
            <h3 className="text-xl font-semibold theme-primary mb-4">Circuit Performance</h3>
            <TableChart 
              data={driver.circuitAverages}
              config={{
                columns: [
                  { key: 'circuit', label: 'Circuit' },
                  { key: 'average', label: 'Average Finish' },
                  { key: 'races', label: 'Races' }
                ],
                sortable: true,
                sortBy: 'average',
                sortDirection: 'asc',
                pagination: true,
                pageSize: 10
              }}
            />
          </div>
        )}

        {/* Grill The Grid Performance */}
        {driver.grillTheGrid && driver.grillTheGrid.length > 0 && (() => {
          // Group data by season and pivot rounds as columns
          const groupedBySeason = {};
          driver.grillTheGrid.forEach(gtg => {
            if (!groupedBySeason[gtg.season]) {
              groupedBySeason[gtg.season] = {
                season: (
                  <Link 
                    to={`/f1/seasons/${gtg.season}`}
                    className="theme-primary hover:theme-accent hover:underline font-medium"
                  >
                    {gtg.season}
                  </Link>
                ),
                totalPoints: 0
              };
            }
            groupedBySeason[gtg.season][`round${gtg.round}`] = gtg.points || '-';
            // Add to total if points exist
            if (gtg.points) {
              groupedBySeason[gtg.season].totalPoints += parseFloat(gtg.points) || 0;
            }
          });

          // Get standings for each season to determine final position
          const seasons = Object.keys(groupedBySeason);
          seasons.forEach(season => {
            const standings = getStandingsByYear(season);
            const position = standings.findIndex(s => s.driverId == driverId) + 1;
            groupedBySeason[season].finalPosition = position > 0 ? position : '-';
          });

          // Convert to array and sort by season descending
          const grillTheGridData = Object.values(groupedBySeason).sort((a, b) => {
            const seasonA = typeof a.season === 'object' ? parseInt(a.season.props.children) : parseInt(a.season);
            const seasonB = typeof b.season === 'object' ? parseInt(b.season.props.children) : parseInt(b.season);
            return seasonB - seasonA;
          });

          // Get all unique rounds from the data
          const rounds = [...new Set(driver.grillTheGrid.map(gtg => gtg.round))].sort((a, b) => a - b);
          const columns = [
            { key: 'season', label: 'Season' },
            { key: 'finalPosition', label: 'Final Position' },
            ...rounds.map(round => ({ key: `round${round}`, label: `Round ${round}` })),
            { key: 'totalPoints', label: 'Total' }
          ];

          return (
            <div className="card">
              <h3 className="text-xl font-semibold theme-primary mb-4">Grill The Grid Performance</h3>
              <TableChart 
                data={grillTheGridData}
                config={{
                  columns,
                  sortable: true,
                  sortBy: 'season',
                  sortDirection: 'desc',
                  pagination: true,
                  pageSize: 10
                }}
              />
            </div>
          );
        })()}
      </div>
    </F1PageTemplate>
  );
};

export default F1DriverDetailPage;
